import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { HTMLProps, useEffect, useState } from 'react';
import { classes } from 'shared';

export function PhoneInput({
  phone = '',
  name = 'Phone',
  error,
  onUpdate,
  onUpdateError,
  clean,
  iClassName,
  className,
  children,
  ...props
}: {
  phone: string | undefined;
  error: string | string[] | undefined;
  /** This modifies the container's classes */
  className?: string;
  iClassName?: string;
  clean?: boolean;
  name?: string;
  children?: React.ReactNode;
  onUpdate: (v: string) => void;
  onUpdateError: (v: string | undefined) => void;
} & HTMLProps<HTMLInputElement>) {
  const [dispPhone, setDispPhone] = useState('');

  useEffect(() => {
    if (!dispPhone && phone) handlePhone(phone);
  }, [phone]);

  return (
    <div className={classes(className, 'relative mt-[8px]')}>
      <label
        htmlFor={name}
        className={classes(
          'absolute -top-2 left-2 z-[1] -mt-px inline-block select-none bg-white px-1 text-xs font-medium text-gray-900',
          (className?.includes('md:pl-2') || className?.includes('md:px-2')) && 'md:ml-2',
          props.disabled && 'bg-gradient-to-b from-white via-white to-gray-200',
          props.placeholder && 'hidden',
        )}>
        {name}
        {children}
      </label>
      <div className="relative">
        <input
          {...props}
          className={calculateClassName()}
          type="text"
          name={name}
          id={name}
          autoComplete="tel"
          value={dispPhone}
          onChange={e => handlePhone(e.target.value)}
          onBlur={() => validatePhone(phone)}
          aria-describedby="phone-error"
        />
        {!!getError() && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        )}
      </div>
      <span id="phone-error" className="text-sm text-red-600">
        {getError()}
      </span>
    </div>
  );

  function getError() {
    if (!error) return;
    if (typeof error !== 'string') return error?.[0];
    return error;
  }

  function calculateClassName() {
    if (clean) return iClassName;
    return classes(
      iClassName,
      getError()
        ? 'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      'relative block w-full cursor-text rounded-md border bg-white px-4 py-3 pl-3 text-left placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-600',
    );
  }

  function handlePhone(v: string) {
    if (!v) {
      setDispPhone('');
      onUpdate('');
      return;
    }
    const s = v.replace(/\D/g, '');
    const phone = `(${s.slice(0, 3)}${s.length > 3 ? ') ' + s.slice(3, 6) : ''}${
      s.length > 6 ? '-' + s.slice(6, 10) : ''
    }`;
    onUpdate(phone.replace(/\D/g, ''));
    setDispPhone(phone);
    if (getError()) {
      onUpdateError(undefined);
    }
  }

  function validatePhone(phone?: string) {
    if (!phone) return;
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    const valid = phoneRegex.test(String(phone));
    if (!valid) onUpdateError('Invalid phone number');
    return valid;
  }
}
